// Shared constants, pure helpers, and small presentational sub-components
// used by two or more of index.js (List view), AddProduct.js and
// EditProduct.js. Split out 2026-09-09 when the previously-combined
// SupportAdmin screen (list + Add Product panel + Edit Product panel all in
// one 1700-line file) was broken into one file per concern. Anything that
// was only ever used by a single one of those three screens stayed local to
// that file instead of being pulled in here.
import React from "react";
import * as Mui from "@mui/material";
import AppsIcon from "@mui/icons-material/Apps";
import AppsOutlinedIcon from "@mui/icons-material/AppsOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import BeachAccessOutlinedIcon from "@mui/icons-material/BeachAccessOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import ExtensionIcon from "@mui/icons-material/Extension";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import FingerprintOutlinedIcon from "@mui/icons-material/FingerprintOutlined";
import HomeWorkOutlinedIcon from "@mui/icons-material/HomeWorkOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MoreTimeOutlinedIcon from "@mui/icons-material/MoreTimeOutlined";
import MyLocationOutlinedIcon from "@mui/icons-material/MyLocationOutlined";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import PhoneAndroidOutlinedIcon from "@mui/icons-material/PhoneAndroidOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import dayjs from "dayjs";
import { CustomAutoComplete } from "components/CustomComponents/CustomAutoComplete";
import { CustomDatePicker } from "components/CustomComponents/CustomDatePicker";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import {
  PrimaryButton,
  SecondaryButton,
} from "components/CustomComponents/DesignSystemButtons";
import {
  GlobalFieldLabel,
  globalTextFieldSx,
  sharedTableBodyCellSx,
  sharedTableHeadCellSx,
} from "components/DesignSystem/sharedUiPatterns";
import { tooltipStyleProps } from "services/constants/Styles";
import {
  designColors,
  designRadii,
  StatCard,
} from "services/constants/designTokens";

export const ACCESS_TYPES = [
  {
    value: "Trial",
    label: "Trial",
    description:
      "Temporary access for a real customer organization using their own data. Expires automatically.",
  },
  {
    value: "Licensed",
    label: "Licensed",
    description: "Paid customer access for the purchased period.",
  },
  {
    value: "Internal",
    label: "Internal",
    description:
      "For OrganFlow internal use only. No expiry and not counted for commercial licensing.",
  },
];

// Backend's access_type enum is lowercase (Django TextChoices-style: TRIAL =
// "trial", "Trial" / LICENSED = "licensed", "Licensed" / INTERNAL =
// "internal", "Internal"). ACCESS_TYPES.value above stays capitalized on our
// own side -- it's threaded through state, validation and UI comparisons
// (selected.access_type !== "Internal") throughout -- this maps only at the
// outgoing payload edge, in AddProduct's/EditProduct's own save() calls.
export const ACCESS_TYPE_API_VALUES = {
  Trial: "trial",
  Licensed: "licensed",
  Internal: "internal",
};
// Inverse of ACCESS_TYPE_API_VALUES, for reading access_type back off the
// company-features GET response -- case-normalized in case the backend ever
// sends "TRIAL"/"Trial" instead of "trial".
export const ACCESS_TYPE_LABELS = {
  trial: "Trial",
  licensed: "Licensed",
  internal: "Internal",
};

export const ACCESS_DATE_MIN = dayjs("2000-01-01");
export const ACCESS_DATE_MAX = dayjs().add(50, "year");
export const ACCESS_DATE_ERROR_MESSAGE = {
  minDate: `Date can't be before ${ACCESS_DATE_MIN.format("DD/MM/YYYY")}.`,
  maxDate: `Date can't be after ${ACCESS_DATE_MAX.format("DD/MM/YYYY")}.`,
  invalidDate: "Enter a valid date.",
};

export const ACCESS_FIELD_DEFAULTS = {
  access_type: "Licensed",
  start_date: "",
  access_until: "",
  auto_expire: true,
};

// Maps one product/access/company-features/ GET response row onto this
// screen's own internal product shape. id/name are renamed from
// product_id/product_name -- the List view's `products` state, snapshot()
// below, and every consumer (table rows, EditProduct's `product` prop) keys
// off `id`/`name`. end_date is renamed to access_until (the reverse of the
// access_until -> end_date rename at save() time); access_type is read back
// through ACCESS_TYPE_LABELS (the reverse of ACCESS_TYPE_API_VALUES).
// `item` is one row of the `products` array inside GET
// product/access/company-features/ (no company_id) response (confirmed
// 2026-09-11 -- the real response is { total_products, total_product_count,
// total_features, basic_features_count, addon_features_count,
// basic_features_enabled, addon_features_enabled, products: [...] }, NOT a
// bare array as an earlier pass through this file assumed; see index.js's
// own fetch comment for how the wrapper's aggregate fields are used). Each
// row: { id, company_id, company_name, product_id, product_name, is_active,
// display_order, start_date, end_date, access_type, auto_expire,
// modified_by, modified_at, features: [{feature_id, feature_name,
// feature_code, is_active}] }. The row already carries its own product_name,
// but not description or total/basic/addon feature counts -- those are
// looked up from the product catalog by product_id via `catalogById` (see
// product-catalog/), with the row's own product_name preferred since it
// needs no other fetch to have resolved first.
export const mapCompanyProduct = (item, catalogById = {}) => {
  const catalog = catalogById[item.product_id] || {};
  return {
    id: item.product_id,
    // The access record's own primary key (distinct from product_id above,
    // which `id` is keyed on everywhere else) -- EditProduct's save() needs
    // this back on PUT so the backend knows which existing row to update,
    // not just which company+product it belongs to.
    access_record_id: item.id,
    name: item.product_name || catalog.name || "",
    description: catalog.description || "",
    access_type:
      ACCESS_TYPE_LABELS[String(item.access_type || "").toLowerCase()] ||
      ACCESS_FIELD_DEFAULTS.access_type,
    start_date: item.start_date || ACCESS_FIELD_DEFAULTS.start_date,
    access_until: item.end_date || ACCESS_FIELD_DEFAULTS.access_until,
    auto_expire:
      item.auto_expire !== undefined
        ? item.auto_expire !== false
        : ACCESS_FIELD_DEFAULTS.auto_expire,
    is_active: item.is_active !== undefined ? Boolean(item.is_active) : true,
    display_order: item.display_order ?? catalog.display_order ?? 0,
    // The "Last Updated" column's source -- confirmed 2026-09-11 the real
    // field is `modified_at`, not `updated_at` (this file's own internal/UI
    // key, kept as-is so the table/header array didn't need renaming too --
    // same "rename only at the mapper boundary" convention as end_date ->
    // access_until above). Before this fix the key was never populated at
    // all, so every row silently showed "--" regardless of real data.
    updated_at: item.modified_at || null,
    // Total/basic/add-on counts are a catalog-level fact (fixed per
    // product), not something the per-company access record carries -- same
    // catalog counts the panels' own mini-cards use.
    // The company-features row now carries its own per-product counts;
    // prefer those, falling back to the catalog for older responses.
    basic_features_count:
      item.basic_features_count ?? catalog.basic_features_count ?? 0,
    addon_features_count:
      item.addon_features_count ?? catalog.addon_features_count ?? 0,
    features_count:
      item.total_features ??
      (item.basic_features_count != null || item.addon_features_count != null
        ? (item.basic_features_count ?? 0) + (item.addon_features_count ?? 0)
        : (catalog.total_features ??
          (catalog.basic_features_count ?? 0) +
            (catalog.addon_features_count ?? 0))),
    features: item.features,
  };
};

export const snapshot = (items) =>
  JSON.stringify(
    items.map(
      ({
        id,
        is_active,
        access_type,
        start_date,
        access_until,
        auto_expire,
        display_order,
      }) => ({
        id,
        is_active: Boolean(is_active),
        access_type,
        start_date,
        access_until,
        auto_expire: auto_expire !== false,
        display_order,
      }),
    ),
  );

export const addon = (item) =>
  Boolean(
    item.is_addon ||
    item.isAddon ||
    item.type === "addon" ||
    item.feature_type === "addon" ||
    String(item.feature_type).toUpperCase() === "ADD_ON",
  );

export const description = (item) =>
  item.description ||
  item.short_description ||
  `${item.name} configuration and access management`;

const FEATURE_ICON_KEYWORDS = [
  [["attendance"], AccessTimeOutlinedIcon],
  [["leave"], EventBusyOutlinedIcon],
  [["shift"], ScheduleOutlinedIcon],
  [["holiday"], BeachAccessOutlinedIcon],
  [["directory", "employee"], PeopleOutlineIcon],
  [["notification", "alert"], NotificationsNoneOutlinedIcon],
  [["overtime"], MoreTimeOutlinedIcon],
  [["expense"], ReceiptLongOutlinedIcon],
  [["regulariz"], FactCheckOutlinedIcon],
  [["geo", "location", "fenc"], MyLocationOutlinedIcon],
  [["home"], HomeWorkOutlinedIcon],
  [["mobile"], PhoneAndroidOutlinedIcon],
  [["biometric", "fingerprint"], FingerprintOutlinedIcon],
];
export const featureIcon = (name) => {
  const lower = name.trim().toLowerCase();
  const match = FEATURE_ICON_KEYWORDS.find(([keywords]) =>
    keywords.some((keyword) => lower.includes(keyword)),
  );
  return match ? match[1] : AppsOutlinedIcon;
};

export const chipSx = (active) => ({
  height: 23,
  fontSize: 11,
  fontWeight: 600,
  color: active ? designColors.tealDark : "#B42318",
  bgcolor: active ? designColors.tealTint : "#FEF3F2",
  border: `1px solid ${active ? "#B7E4DB" : "#FECDCA"}`,
});

export const switchSx = {
  "& .MuiSwitch-switchBase.Mui-checked": {
    color: designColors.teal,
  },
  "& .MuiSwitch-switchBase.Mui-checked:hover": {
    bgcolor: designColors.tealTint,
  },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    bgcolor: designColors.teal,
  },
};

export const formatDate = (value) =>
  value
    ? new Date(value).toLocaleDateString(undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "";
export const daysLeft = (value) =>
  Math.ceil((new Date(value) - new Date()) / 86400000);

export const accessStatusLabel = (item) => {
  if (!item.access_type) return item.is_active ? "Active" : "Inactive";
  const expired =
    item.access_type !== "Internal" &&
    item.auto_expire !== false &&
    item.access_until &&
    daysLeft(item.access_until) < 0;
  return expired ? "Expired" : item.access_type;
};
export const isEffectivelyActive = (item) =>
  item.access_type
    ? accessStatusLabel(item) !== "Expired"
    : Boolean(item.is_active);

const panelHead = {
  ...sharedTableHeadCellSx,
  position: "static",
  top: "auto",
  zIndex: "auto",
  py: 0.7,
  px: 1,
  fontSize: 10.5,
  color: designColors.slate700,
  whiteSpace: "nowrap",
};
const panelCell = {
  ...sharedTableBodyCellSx,
  py: 0.9,
  px: 1,
  fontSize: 11.5,
  verticalAlign: "top",
};

// Renders one Basic- or Add-on-features table inside the Add/Edit panel.
// Identical between the two panels, so it lives here rather than being
// copy-pasted -- only the underlying data (and, previously, which product
// they belonged to) ever differed.
export const featureTable = (items, sourceItems, onToggle) => (
  <Mui.TableContainer
    sx={{ border: `1px solid ${designColors.slate200}`, borderRadius: 1 }}
  >
    <Mui.Table size="small">
      <Mui.TableHead sx={{ bgcolor: designColors.slateBg }}>
        <Mui.TableRow>
          <Mui.TableCell sx={panelHead}>Feature</Mui.TableCell>
          <Mui.TableCell sx={panelHead}>Impacted Modules</Mui.TableCell>
          <Mui.TableCell sx={panelHead} align="right">
            Enabled
          </Mui.TableCell>
        </Mui.TableRow>
      </Mui.TableHead>
      <Mui.TableBody>
        {items.map((item) => {
          const IconComponent = featureIcon(item.name);
          // Real per-feature data from product-feature-config/products/
          // (impacted_modules, with module_group_names as a fallback).
          const impactedModules = Array.isArray(item.impacted_modules) &&
            item.impacted_modules.length
            ? item.impacted_modules
            : item.module_group_names;
          return (
            <Mui.TableRow key={item.id}>
              <Mui.TableCell sx={{ ...panelCell, fontWeight: 600 }}>
                <Mui.Stack direction="row" spacing={1} alignItems="center">
                  <Mui.Box
                    sx={{
                      width: 26,
                      height: 26,
                      flexShrink: 0,
                      display: "grid",
                      placeItems: "center",
                      borderRadius: 1,
                      color: item.is_active
                        ? designColors.tealDark
                        : designColors.slate500,
                      bgcolor: item.is_active
                        ? designColors.tealTint
                        : designColors.slate100,
                    }}
                  >
                    <IconComponent sx={{ fontSize: 15 }} />
                  </Mui.Box>
                  <span>{item.name}</span>
                </Mui.Stack>
              </Mui.TableCell>
              <Mui.TableCell
                sx={{
                  ...panelCell,
                  color: designColors.slate600,
                  whiteSpace: "normal",
                  overflow: "visible",
                  textOverflow: "clip",
                }}
              >
                {Array.isArray(impactedModules) && impactedModules.length
                  ? impactedModules.join(", ")
                  : "--"}
              </Mui.TableCell>
              <Mui.TableCell align="right" sx={panelCell}>
                <Mui.Switch
                  checked={Boolean(item.is_active)}
                  onChange={(event) => onToggle(item.id, event.target.checked)}
                  size="small"
                  sx={switchSx}
                />
              </Mui.TableCell>
            </Mui.TableRow>
          );
        })}
        {!items.length && (
          <TableEmptyState dataLength={sourceItems} colSpan={3} rowHeight={160} />
        )}
      </Mui.TableBody>
    </Mui.Table>
  </Mui.TableContainer>
);

// The "Total Feats / Basic / Add-ons" mini-card row shown at the top of both
// the Add Product and Edit Product panels.
export const ProductFeatureStatCards = ({ total, basic, addons }) => (
  <Mui.Box
    sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1.5 }}
  >
    <StatCard
      icon={AppsIcon}
      title="Total Feats"
      value={total}
      accent="#1677FF"
      tint="#DCEEFF"
      iconRadius={designRadii.iconButton}
      iconSize={32}
      variant="tinted"
    />
    <StatCard
      icon={CheckCircleIcon}
      title="Basic"
      value={basic}
      accent={designColors.tealDark}
      tint="#D7F3EA"
      iconRadius={designRadii.iconButton}
      iconSize={32}
      variant="tinted"
    />
    <StatCard
      icon={ExtensionIcon}
      title="Add-ons"
      value={addons}
      accent="#EA6A00"
      tint="#FFE8D1"
      iconRadius={designRadii.iconButton}
      iconSize={32}
      variant="tinted"
    />
  </Mui.Box>
);

// The Access Type / Start Date / Access Until / Auto Expire block, identical
// between Add Product and Edit Product. `onFieldChange` is called as
// (field, value) -- each panel supplies its own state setter.
export const AccessValiditySection = ({
  selected,
  dateError,
  setDateError,
  onFieldChange,
}) => {
  if (!selected) return null;
  return (
    <Mui.Stack spacing={2}>
      <Mui.Typography
        sx={{ fontSize: 14, fontWeight: 600, color: designColors.slate900 }}
      >
        Access Validity
      </Mui.Typography>
      <Mui.Box>
        <GlobalFieldLabel helperText="Define the access period for this product.">
          Access Type
          <Mui.Tooltip
            arrow
            placement="right"
            componentsProps={tooltipStyleProps}
            title={
              <Mui.Stack spacing={0.85} sx={{ py: 0.25 }}>
                <Mui.Typography sx={{ fontSize: 12, fontWeight: 700 }}>
                  Access Type guide
                </Mui.Typography>
                {ACCESS_TYPES.map((type) => (
                  <Mui.Stack key={type.value} direction="row" spacing={1}>
                    <Mui.Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        width: 52,
                        flexShrink: 0,
                      }}
                    >
                      {type.label}
                    </Mui.Typography>
                    <Mui.Typography sx={{ fontSize: 11 }}>
                      {type.description}
                    </Mui.Typography>
                  </Mui.Stack>
                ))}
              </Mui.Stack>
            }
          >
            <InfoOutlinedIcon
              sx={{
                fontSize: 15,
                ml: 0.5,
                verticalAlign: "text-bottom",
                color: designColors.slate400,
                cursor: "pointer",
              }}
            />
          </Mui.Tooltip>
        </GlobalFieldLabel>
        <CustomAutoComplete
          dataList={ACCESS_TYPES}
          selectedValue={
            ACCESS_TYPES.find(
              (type) => type.value === (selected.access_type || "Licensed"),
            )?.label || ""
          }
          updateDataFunction={(event, value) =>
            onFieldChange("access_type", value?.value || "Licensed")
          }
          placeHolder="Select an access type"
        />
      </Mui.Box>
      {selected.access_type !== "Internal" && (
        <Mui.Stack direction="row" spacing={1.5}>
          <Mui.Box sx={{ flex: 1 }}>
            <GlobalFieldLabel helperText="When this product's access period begins.">
              Start Date
            </GlobalFieldLabel>
            <CustomDatePicker
              value={selected.start_date ? dayjs(selected.start_date) : null}
              minDate={ACCESS_DATE_MIN}
              maxDate={
                selected.access_until
                  ? dayjs(selected.access_until)
                  : ACCESS_DATE_MAX
              }
              onChange={(newValue) => {
                setDateError((prev) => ({ ...prev, start_date: undefined }));
                onFieldChange("start_date", newValue.format("YYYY-MM-DD"));
              }}
              onError={(reason) =>
                setDateError((prev) => ({
                  ...prev,
                  start_date: reason
                    ? ACCESS_DATE_ERROR_MESSAGE[reason] || "Enter a valid date."
                    : prev.start_date,
                }))
              }
              error={Boolean(dateError.start_date)}
              helperText={dateError.start_date || ""}
            />
          </Mui.Box>
          <Mui.Box sx={{ flex: 1 }}>
            <GlobalFieldLabel helperText="When this product's access period ends.">
              Access Until
            </GlobalFieldLabel>
            <CustomDatePicker
              value={
                selected.access_until ? dayjs(selected.access_until) : null
              }
              minDate={
                selected.start_date ? dayjs(selected.start_date) : ACCESS_DATE_MIN
              }
              maxDate={ACCESS_DATE_MAX}
              onChange={(newValue) => {
                setDateError((prev) => ({ ...prev, access_until: undefined }));
                onFieldChange("access_until", newValue.format("YYYY-MM-DD"));
              }}
              onError={(reason) =>
                setDateError((prev) => ({
                  ...prev,
                  access_until: reason
                    ? ACCESS_DATE_ERROR_MESSAGE[reason] || "Enter a valid date."
                    : prev.access_until,
                }))
              }
              error={Boolean(dateError.access_until)}
              helperText={dateError.access_until || ""}
            />
          </Mui.Box>
        </Mui.Stack>
      )}
      {selected.access_type !== "Internal" && (
        <Mui.Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            p: 2,
            bgcolor: designColors.slateBg,
            border: `1px solid ${designColors.slate200}`,
            borderRadius: designRadii.banner,
          }}
        >
          <GlobalFieldLabel helperText="Automatically disable access after the Access Until date.">
            Auto Expire
          </GlobalFieldLabel>
          <Mui.Switch
            size="small"
            sx={switchSx}
            checked={selected.auto_expire !== false}
            onChange={(event) =>
              onFieldChange("auto_expire", event.target.checked)
            }
          />
        </Mui.Stack>
      )}
      {selected.access_type !== "Internal" &&
        selected.start_date &&
        selected.access_until && (
          <Mui.Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            sx={{ p: 1, bgcolor: designColors.tealTint, borderRadius: 1 }}
          >
            <Mui.Typography
              sx={{ fontSize: 11, color: designColors.tealDarker }}
            >
              This product and its features will be available from{" "}
              {formatDate(selected.start_date)} to{" "}
              {formatDate(selected.access_until)}.
            </Mui.Typography>
          </Mui.Stack>
        )}
    </Mui.Stack>
  );
};

// The search box + Basic Features + Add-on Features tables, identical
// between Add Product and Edit Product.
export const ProductFeaturesSection = ({
  featureSearch,
  setFeatureSearch,
  basicFeatures,
  addonFeatures,
  onToggleFeature,
}) => {
  const filtered = (items) =>
    items.filter((item) =>
      item.name.toLowerCase().includes(featureSearch.toLowerCase()),
    );
  return (
    <React.Fragment>
      <Mui.TextField
        size="small"
        placeholder="Search features"
        value={featureSearch}
        onChange={(event) => setFeatureSearch(event.target.value)}
        InputProps={{
          startAdornment: (
            <Mui.InputAdornment position="start">
              <SearchOutlinedIcon fontSize="small" />
            </Mui.InputAdornment>
          ),
        }}
        sx={globalTextFieldSx}
      />
      <Mui.Stack direction="row" justifyContent="space-between">
        <Mui.Typography
          sx={{ fontSize: 13, fontWeight: 700, color: designColors.tealDark }}
        >
          Basic Features (Included)
        </Mui.Typography>
        <Mui.Chip
          size="small"
          label={`${basicFeatures.filter((item) => item.is_active).length} of ${basicFeatures.length} enabled`}
          sx={chipSx(true)}
        />
      </Mui.Stack>
      <Mui.Typography sx={{ mt: -1, fontSize: 11, color: designColors.slate600 }}>
        These features are included with the selected product.
      </Mui.Typography>
      {featureTable(filtered(basicFeatures), basicFeatures, onToggleFeature)}
      <Mui.Stack direction="row" justifyContent="space-between">
        <Mui.Typography sx={{ fontSize: 13, fontWeight: 700, color: "#EA6A00" }}>
          Add-on Features (Optional)
        </Mui.Typography>
        <Mui.Chip
          size="small"
          label={`${addonFeatures.filter((item) => item.is_active).length} of ${addonFeatures.length} enabled`}
          sx={chipSx(true)}
        />
      </Mui.Stack>
      <Mui.Typography sx={{ mt: -1, fontSize: 11, color: designColors.slate600 }}>
        These features are optional and can be enabled as needed.
      </Mui.Typography>
      {featureTable(filtered(addonFeatures), addonFeatures, onToggleFeature)}
    </React.Fragment>
  );
};

// The Cancel + (tooltip-wrapped, All-Companies-aware) Save Configuration
// button row, identical between Add Product and Edit Product footers.
export const SaveConfigurationFooter = ({
  onCancel,
  onSave,
  saving,
  disabled,
  disabledReason,
}) => (
  <Mui.Stack direction="row" spacing={1.5} justifyContent="flex-end">
    <SecondaryButton onClick={onCancel}>Cancel</SecondaryButton>
    <Mui.Tooltip
      arrow
      placement="top"
      componentsProps={tooltipStyleProps}
      title={disabled ? disabledReason : ""}
    >
      {/* span wrapper so the tooltip still fires on hover once the button is
          disabled -- a disabled native/MUI button has pointer-events:none
          and swallows hover by itself. */}
      <span>
        <PrimaryButton onClick={onSave} loading={saving} disabled={disabled}>
          Save Configuration
        </PrimaryButton>
      </span>
    </Mui.Tooltip>
  </Mui.Stack>
);
